// A catechism or confession from the library (Resources), read in full: questions and answers, or
// chapters and paragraphs, each with its Scripture proofs opening their passage pages. The daily card
// links here by question; the reader (/books/$id) has the same works section by section.
import { createFileRoute, Link } from "@tanstack/react-router";
import { Empty } from "../../components/Empty";
import ui from "../../components/ui.module.css";
import { useLibraryWork } from "../../data/library";
import { useTitle } from "../../lib/useTitle";
import s from "./resources.module.css";

export const Route = createFileRoute("/_app/library/$id")({ component: LibraryWorkPage });

function Proofs({ refs }: { refs: string[] }) {
  if (!refs.length) return null;
  return (
    <p className={s.meta}>
      {refs.map((r, i) => (
        <span key={`${r}-${i}`}>
          {i > 0 && "; "}
          <Link to="/r/$ref" params={{ ref: r }}>
            {r}
          </Link>
        </span>
      ))}
    </p>
  );
}

function LibraryWorkPage() {
  const { id } = Route.useParams();
  const { data: work, isPending, isError } = useLibraryWork(id);
  useTitle(work?.title ?? "Library");
  if (isPending) return <div className={ui.page} aria-busy="true" />;
  if (isError || !work)
    return (
      <div className={ui.page}>
        <Empty title="This isn’t in the library.">
          <Link to="/resources" className={ui.secondary}>
            Back to Resources
          </Link>
        </Empty>
      </div>
    );

  return (
    <div className={ui.page}>
      <header className={s.head}>
        <h1 className={ui.title}>{work.title}</h1>
        <p className={ui.subtitle}>
          {[work.author, work.written].filter(Boolean).join(", ")}. {work.license}.
        </p>
        <p className={s.meta}>
          <Link to="/books/$id" params={{ id: work.id }}>
            Read it section by section, with its notes
          </Link>
        </p>
      </header>
      {work.questions && (
        <ol className={s.list}>
          {work.questions.map((q) => (
            <li key={q.n} id={`q${q.n}`}>
              <div>
                <p className={s.name}>
                  {q.n}. {q.q}
                </p>
                {q.a && <p className={s.text}>{q.a}</p>}
                <Proofs refs={q.proofs} />
              </div>
            </li>
          ))}
        </ol>
      )}
      {work.chapters?.map((c) => (
        <section key={c.n} className={s.section} id={`c${c.n}`} aria-labelledby={`c${c.n}-head`}>
          <h2 id={`c${c.n}-head`} className={s.title}>
            {c.n}. {c.title}
          </h2>
          {c.paragraphs.map((p, i) => (
            <div key={i}>
              <p className={s.text}>
                {p.n !== null && <strong>{p.n}. </strong>}
                {p.text}
              </p>
              <Proofs refs={p.proofs} />
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}
